import type { Session, Project } from '@/api/client';
import { getBaseStatus } from '@/utils/projectStatuses';

/** Проекты в зоне «Далее:» — только Добавлен / Запись и без 100% прогресса чтеца по книге */
const NEXT_PROJECT_BASE_STATUSES = new Set(['Добавлен', 'Запись']);

export interface ReaderProjectIndexEntry {
  projectId: string;
  projectName: string;
  deadline: string | null;
  /** Базовый статус проекта (без подстатуса) */
  baseState: string;
  /** Прогресс чтеца в этом проекте, 0..100; null — данных нет */
  readerProgress: number | null;
}

function parseReaderProgress(raw: number | string | null | undefined): number | null {
  if (raw == null) return null;
  const n = typeof raw === 'string' ? parseFloat(raw) : raw;
  return isNaN(n) ? null : n;
}

function scalarSplitKey(v: unknown): string {
  if (v == null || v === '') return '';
  return String(v);
}

function numericSplitKey(v: unknown): string {
  if (v == null || v === '') return '';
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? String(n) : String(v);
}

function deadlineSplitKey(v: string | null | undefined): string {
  if (!v) return '';
  return String(v).slice(0, 10);
}

/**
 * Сравнивает только поля, от которых зависят split/«Далее:» на сетке.
 * notes/files/sessions и прочий realtime-шум не должны пересобирать расписание.
 */
export function projectScheduleSplitFieldsEqual(a: Project, b: Project): boolean {
  if (a.id !== b.id) return false;
  if (scalarSplitKey(a.name) !== scalarSplitKey(b.name)) return false;
  if (scalarSplitKey(a.state) !== scalarSplitKey(b.state)) return false;
  if (deadlineSplitKey(a.deadline) !== deadlineSplitKey(b.deadline)) return false;
  const ar = a.readers ?? [];
  const br = b.readers ?? [];
  if (ar.length !== br.length) return false;
  for (let i = 0; i < ar.length; i++) {
    if (ar[i].id !== br[i].id) return false;
    if (numericSplitKey(ar[i].progress) !== numericSplitKey(br[i].progress)) return false;
    if (numericSplitKey(ar[i].remaining_sessions) !== numericSplitKey(br[i].remaining_sessions)) {
      return false;
    }
  }
  return true;
}

/** Стабильный ключ split-полей списка проектов — чтобы не пересчитывать индексы при новом массиве с тем же смыслом. */
export function getProjectsScheduleSplitKey(projects: Project[]): string {
  return projects
    .map((p) => {
      const readers = (p.readers ?? [])
        .map((r) => `${r.id}:${numericSplitKey(r.progress)}:${numericSplitKey(r.remaining_sessions)}`)
        .join(',');
      return `${p.id}\t${scalarSplitKey(p.name)}\t${scalarSplitKey(p.state)}\t${deadlineSplitKey(p.deadline)}\t${readers}`;
    })
    .join('\n');
}

function sessionCoefValue(raw: Session['coef']): number {
  if (raw == null) return 1;
  const n = parseFloat(String(raw));
  return isNaN(n) ? 1 : n;
}

/** Сумма coef запланированных сессий по парам project+reader (те же фильтры, что у групп split/excess). */
export function buildPlannedCoefByReaderProject(sessions: Session[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const s of sessions) {
    if (s.is_canceled) continue;
    if ((s.session_type ?? 'запись') !== 'запись') continue;
    if (s.session_subtype === 'podcast') continue;
    if (s.is_recorded) continue;
    if (!s.project_id) continue;
    const readerId = s.reader_id ?? '';
    const key = `${s.project_id}|${readerId}`;
    map.set(key, (map.get(key) ?? 0) + sessionCoefValue(s.coef));
  }
  return map;
}

/** Оставляет проекты для «Далее:» по правилам планировщика */
function filterEligibleNextProjects(
  entries: ReaderProjectIndexEntry[],
  readerId: string,
  indexes: Pick<ProjectIndexes, 'remainingMap' | 'readerProjectsIndex'>,
  plannedCoefMap: Map<string, number>,
  excludeProjectId?: string,
): ReaderProjectIndexEntry[] {
  const { remainingMap, readerProjectsIndex } = indexes;
  return entries.filter((e) => {
    if (excludeProjectId && e.projectId === excludeProjectId) return false;
    if (!NEXT_PROJECT_BASE_STATUSES.has(e.baseState)) return false;
    const rp = e.readerProgress;
    if (rp != null && !isNaN(rp) && rp >= 100) return false;
    const remaining = getEffectiveRemainingForReaderProject(
      remainingMap,
      readerProjectsIndex,
      e.projectId,
      readerId,
    );
    const planned = plannedCoefMap.get(`${e.projectId}|${readerId}`) ?? 0;
    if (remaining != null && !isNaN(remaining) && planned >= remaining) return false;
    return true;
  });
}

function nextProjectNamesFromEntries(entries: ReaderProjectIndexEntry[]): string[] | null {
  const names = entries.map((e) => e.projectName);
  return names.length > 0 ? names : null;
}

/**
 * Оставшиеся условные сессии по данным project_readers (0 = книга закрыта).
 * Если в карте нет записи, но прогресс чтеца по проекту ≥100% — считаем remaining = 0.
 */
function getEffectiveRemainingForReaderProject(
  remainingMap: Map<string, Map<string, number>>,
  readerProjectsIndex: Map<string, ReaderProjectIndexEntry[]>,
  projectId: string,
  readerId: string,
): number | null {
  const v = remainingMap.get(projectId)?.get(readerId);
  if (v != null && !isNaN(v)) return v;
  const entries = readerProjectsIndex.get(readerId) ?? [];
  const e = entries.find((x) => x.projectId === projectId);
  if (e?.readerProgress != null && !isNaN(e.readerProgress) && e.readerProgress >= 100) return 0;
  return null;
}

/** Live reader_progress с сессий (realtime) — книга уже закрыта. */
function groupHasReaderProgressComplete(groupSessions: Session[]): boolean {
  for (const s of groupSessions) {
    const rp = s.reader_progress;
    if (rp != null && !isNaN(Number(rp)) && Number(rp) >= 100) return true;
    const fromReaders = s.readers?.find((r) => r.id === (s.reader_id ?? ''))?.progress;
    if (fromReaders != null && !isNaN(Number(fromReaders)) && Number(fromReaders) >= 100) return true;
  }
  return false;
}

export interface SessionFinalStatus {
  /** 'final' = первая сессия где накопленный coef превышает remaining_sessions (место разреза)
   *  'excess' = сессии полностью за пределами remaining_sessions */
  status: 'final' | 'excess';
  /** Доля сессии, принадлежащая текущему проекту (0..1).
   *  Для 'excess' всегда 0. Для 'final' = (remaining - prevCumCoef) / coef */
  splitRatio: number;
  /** Имена следующих проектов чтеца, null = «Нет проекта» */
  nextProjectNames: string[] | null;
}

export interface ProjectIndexes {
  /** projectId → Map<readerId, remaining_sessions> */
  remainingMap: Map<string, Map<string, number>>;
  /** readerId → отсортированный по дедлайну список проектов */
  readerProjectsIndex: Map<string, ReaderProjectIndexEntry[]>;
}

/**
 * Строит индексы проектов для computeFinalSessionStatuses.
 * Вычисляется один раз при изменении projects — кешируется отдельно от сессий.
 */
export function buildProjectIndexes(projects: Project[]): ProjectIndexes {
  const remainingMap = new Map<string, Map<string, number>>();
  const readerProjectsIndex = new Map<string, ReaderProjectIndexEntry[]>();

  for (const p of projects) {
    const baseState = getBaseStatus(p.state);
    for (const r of p.readers ?? []) {
      if (!readerProjectsIndex.has(r.id)) readerProjectsIndex.set(r.id, []);
      readerProjectsIndex.get(r.id)!.push({
        projectId: p.id,
        projectName: p.name,
        deadline: p.deadline ?? null,
        baseState,
        readerProgress: parseReaderProgress(r.progress),
      });
    }

    if (!p.readers?.length) continue;
    const rMap = new Map<string, number>();
    for (const r of p.readers) {
      if (r.remaining_sessions != null) {
        const val =
          typeof r.remaining_sessions === 'string'
            ? parseFloat(r.remaining_sessions)
            : r.remaining_sessions;
        if (!isNaN(val) && val >= 0) rMap.set(r.id, val);
      } else {
        const rp = parseReaderProgress(r.progress);
        if (rp != null && !isNaN(rp) && rp >= 100) rMap.set(r.id, 0);
      }
    }
    if (rMap.size > 0) remainingMap.set(p.id, rMap);
  }

  for (const arr of readerProjectsIndex.values()) {
    arr.sort((a, b) => {
      if (!a.deadline && !b.deadline) return 0;
      if (!a.deadline) return 1;
      if (!b.deadline) return -1;
      return a.deadline.localeCompare(b.deadline);
    });
  }

  return { remainingMap, readerProjectsIndex };
}

/**
 * Вычисляет статусы финальных/избыточных сессий на основе pre-built индексов проектов.
 *
 * ВАЖНО: sessions должны быть предварительно отсортированы по (date, start) —
 * функция не делает внутреннюю сортировку групп, что устраняет O(n log n) на каждый вызов.
 *
 * @param sessions  Сессии, отсортированные по (date, start)
 * @param indexes   Pre-built индексы из buildProjectIndexes()
 */
export function computeFinalSessionStatuses(
  sessions: Session[],
  indexes: ProjectIndexes,
): Map<string, SessionFinalStatus> {
  const result = new Map<string, SessionFinalStatus>();
  const { remainingMap, readerProjectsIndex } = indexes;
  const plannedCoefMap = buildPlannedCoefByReaderProject(sessions);
  const filterCtx = { remainingMap, readerProjectsIndex };

  // Сессии «запись» с чтецом, но без проекта: тот же UI, что у полностью лишней
  // (excess, splitRatio 0, зона «Далее:» со списком следующих проектов чтеца).
  for (const s of sessions) {
    if (s.is_canceled) continue;
    if ((s.session_type ?? 'запись') !== 'запись') continue;
    if (s.session_subtype === 'podcast') continue;
    if (s.is_recorded) continue;
    if (s.project_id) continue;
    const readerId = s.reader_id ?? '';
    if (!readerId) continue;

    const nextProjects = filterEligibleNextProjects(
      readerProjectsIndex.get(readerId) ?? [],
      readerId,
      filterCtx,
      plannedCoefMap,
    );
    const nextProjectNames = nextProjectNamesFromEntries(nextProjects);

    result.set(s.id, { status: 'excess', splitRatio: 0, nextProjectNames });
  }

  // Группируем незаписанные сессии типа «запись» по project+reader.
  // Т.к. sessions уже отсортированы по (date, start), каждая группа
  // автоматически оказывается в хронологическом порядке — sort не нужен.
  const groups = new Map<string, Session[]>();
  for (const s of sessions) {
    if (s.is_canceled) continue;
    if ((s.session_type ?? 'запись') !== 'запись') continue;
    if (s.session_subtype === 'podcast') continue;
    if (s.is_recorded) continue;
    if (!s.project_id) continue;

    const readerId = s.reader_id ?? '';
    const key = `${s.project_id}|${readerId}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(s);
  }

  for (const [key, groupSessions] of groups) {
    const sepIdx = key.indexOf('|');
    const projectId = key.slice(0, sepIdx);
    const readerId = key.slice(sepIdx + 1);

    let remaining = getEffectiveRemainingForReaderProject(
      remainingMap,
      readerProjectsIndex,
      projectId,
      readerId,
    );
    // Live progress с session:updated может опережать кэш проектов (for-schedule-final).
    // Если чтец уже на 100% — remaining считаем 0, даже если в проектах ещё старый остаток.
    if (groupHasReaderProgressComplete(groupSessions)) {
      remaining = 0;
    }
    if (remaining == null) continue;

    const nextProjects = filterEligibleNextProjects(
      readerProjectsIndex.get(readerId) ?? [],
      readerId,
      filterCtx,
      plannedCoefMap,
      projectId,
    );
    const nextProjectNames = nextProjectNamesFromEntries(nextProjects);

    if (remaining <= 0) {
      for (const s of groupSessions) {
        result.set(s.id, { status: 'excess', splitRatio: 0, nextProjectNames });
      }
      continue;
    }

    // Группа уже отсортирована — просто итерируем; remaining > 0
    let cumCoef = 0;
    let cumCoefAtLimit = 0;
    let overLimitIdx = -1;

    for (let i = 0; i < groupSessions.length; i++) {
      const rawCoef = groupSessions[i].coef;
      const sessionCoef = rawCoef != null ? parseFloat(String(rawCoef)) : NaN;
      cumCoef += isNaN(sessionCoef) ? 1 : sessionCoef;
      if (overLimitIdx === -1 && cumCoef > remaining) {
        overLimitIdx = i;
        cumCoefAtLimit = cumCoef;
      }
    }

    if (overLimitIdx === -1) continue;

    const finalSession = groupSessions[overLimitIdx];
    const finalCoef = parseFloat(String(finalSession.coef ?? 1)) || 1;
    const cumBeforeFinal = cumCoefAtLimit - finalCoef;
    const splitRatio = Math.max(0, Math.min(1, (remaining - cumBeforeFinal) / finalCoef));

    result.set(finalSession.id, { status: 'final', splitRatio, nextProjectNames });

    for (let i = overLimitIdx + 1; i < groupSessions.length; i++) {
      result.set(groupSessions[i].id, { status: 'excess', splitRatio: 0, nextProjectNames });
    }
  }

  return result;
}

/**
 * Строит Map<projectId, Map<readerId, remaining_sessions>> — для обратной совместимости.
 * @deprecated Используйте buildProjectIndexes() + computeFinalSessionStatuses() напрямую.
 */
export function buildProjectReadersMap(
  rawData: unknown,
): Map<string, Map<string, number>> {
  const map = new Map<string, Map<string, number>>();
  if (!rawData) return map;

  const projects: Array<{ id: string; readers?: Array<{ id: string; remaining_sessions?: number | string | null }> }> =
    Array.isArray(rawData)
      ? (rawData as typeof projects)
      : ((rawData as { items: typeof projects }).items ?? []);

  for (const p of projects) {
    if (!p.readers?.length) continue;
    const readerMap = new Map<string, number>();
    for (const r of p.readers) {
      if (r.remaining_sessions == null) continue;
      const val =
        typeof r.remaining_sessions === 'string'
          ? parseFloat(r.remaining_sessions)
          : r.remaining_sessions;
      if (!isNaN(val) && val >= 0) readerMap.set(r.id, val);
    }
    if (readerMap.size > 0) map.set(p.id, readerMap);
  }

  return map;
}
